// Shipping tracking a client put in a footage-link box is not a download link. The SAME rule as
// backend/app/link_check/tracking.py (tests/test_hard_drive_tracking.py runs this module and checks they agree):
//   * a URL whose host is a parcel carrier or a parcel-tracking site
//   * a carrier's name, alone ("FedEx")
//   * a bare tracking number: letters and digits (spaces, dashes), 8-40 characters, at least 8 digits
// Ryan, 2026-09-29: tracking "should display somewhere else", and a hard-drive job offers Footage Pull only once the
// team has put the footage somewhere it can pull from (Dropbox, Google Drive, WeTransfer): for a drive, Our Dropbox copy.

export const CARRIER_HOSTS = [
  'fedex.com', 'ups.com', 'usps.com', 'dhl.com', 'dhl.de', 'ontrac.com', 'lasership.com', 'purolator.com',
  'canadapost.ca', 'canadapost-postescanada.ca', 'royalmail.com', 'auspost.com.au', 'gls-group.eu',
  '17track.net', 'parcelsapp.com', 'aftership.com', 'track24.net',
];
export const CARRIER_NAMES = ['fedex', 'ups', 'usps', 'dhl', 'ontrac', 'lasership', 'purolator', 'canada post',
  'royal mail', 'australia post', 'gls'];
const BARE = /^[A-Za-z0-9][A-Za-z0-9 -]{6,38}[A-Za-z0-9]$/;

function hostOf(v) {
  try { return new URL(v.includes('://') ? v : `https://${v}`).hostname.toLowerCase(); } catch { return ''; }
}

export function isTracking(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v) return false;
  if (CARRIER_NAMES.includes(v.toLowerCase())) return true;
  if (v.includes('.') || v.includes('/')) {
    const host = hostOf(v);
    return CARRIER_HOSTS.some(h => host === h || host.endsWith(`.${h}`));
  }
  return BARE.test(v) && (v.match(/\d/g) || []).length >= 8;
}

// One field's values (a string or a list) -> { links, tracking }, split as the server splits (whitespace, commas,
// semicolons) AFTER the whole value is checked, so a tracking number with spaces stays one entry.
export function splitFootage(values) {
  const links = [], tracking = [];
  for (const raw of (Array.isArray(values) ? values : [values])) {
    if (typeof raw !== 'string' || !raw.trim()) continue;
    if (isTracking(raw)) { tracking.push(raw.trim()); continue; }
    for (const piece of raw.split(/[\s,;]+/).filter(Boolean)) (isTracking(piece) ? tracking : links).push(piece);
  }
  const uniq = a => [...new Set(a)];
  return { links: uniq(links), tracking: uniq(tracking) };
}

// A link Footage Pull can take (backend/app/link_check/providers.py detect_provider).
export const isPullable = url => /dropbox\.com|drive\.google\.com|docs\.google\.com|we\.tl\/|wetransfer\.com/i.test(url || '');

// Is there anything on the job Footage Pull can take (the server's auto_advance.has_supported_link)? Once the team has
// set "Our Dropbox copy" it is the job's only link (backend app/footage_copy.py). False for a hard drive not yet brought
// in, and for footage at any link but Dropbox, Google Drive or WeTransfer: the Download footage button is shown off
// then, with why (FootagePullButton.js).
export function hasPullable(project) {
  const p = project || {};
  if ((p.footage_copy_link || '').trim()) return splitFootage(p.footage_copy_link).links.some(isPullable);
  return [p.video_files_links, p.video_files_link, p.photo_files_links, p.photo_files_link, p.files_link]
    .some(v => splitFootage(v).links.some(isPullable));
}

// Is every side of the order sent by hard drive?
export function byHardDrive(project) {
  const p = project || {};
  const sides = p.project_type === 'photo' ? ['photo'] : p.order_type === 'both' ? ['video', 'photo'] : ['video'];
  return sides.every(side => p[`${side}_transfer_method`] === 'Hard Drive');
}

// What the button says under itself while there is nothing to pull: the team is told what to do, an editor only that it
// is coming (R19: nothing an editor reads says how the project is prepared).
export function waitingWords(project, role) {
  if (role === 'editor') return 'This footage is not ready to download yet. We are bringing it in, and this button turns on as soon as it is. Nothing is needed from you.';
  return byHardDrive(project)
    ? 'Nothing to download yet. When the hard drive arrives, upload its footage to our Dropbox and paste that link as Our Dropbox copy, below. This button turns on by itself.'
    : 'Nothing to download yet: this footage is at a link Footage Pull cannot open. Download it by hand (show links), upload it to our Dropbox and paste that link as Our Dropbox copy, below. This button turns on by itself.';
}

// False only for a job whose every transfer is by hard drive and which carries no link Footage Pull can take. "Our
// Dropbox copy" (backend app/footage_copy.py) is such a link: the team put the drive's footage on our Dropbox.
export function hasPullableForHardDrive(project) {
  const p = project || {};
  const sides = p.project_type === 'photo' ? ['photo'] : p.order_type === 'both' ? ['video', 'photo'] : ['video'];
  if (!sides.every(side => p[`${side}_transfer_method`] === 'Hard Drive')) return true;
  const values = [p.footage_copy_link, p.video_files_links, p.video_files_link, p.photo_files_links, p.photo_files_link, p.files_link];
  return values.some(v => splitFootage(v).links.some(isPullable));
}
